<template>
  <div class="recharge-container">
    <div class="recharge-card">
      <div class="recharge-header">
        <h2>Recargas</h2>
        <p>Elige el método de pago para recargar tu cuenta</p>
      </div>

      <!-- Contenido (solo Pago Móvil por ahora; BlockBee / última recarga desactivados) -->
      <template>
        <div class="selected-method-indicator">
          <div class="method-badge method-fiat">
            <i class="fas fa-mobile-alt me-2"></i>
            Pago Móvil
          </div>
        </div>
      </template>

      <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert" aria-live="polite">
        {{ errorMessage }}
        <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Close"></button>
      </div>

      <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert" aria-live="polite">
        {{ successMessage }}
        <button type="button" class="btn-close" @click="successMessage = ''" aria-label="Close"></button>
      </div>

      <!-- Mensaje al volver de BlockBee (success) -->
      <div v-if="blockbeeReturnMessage" class="alert alert-dismissible fade show" :class="blockbeeReturnType === 'success' ? 'alert-success' : 'alert-info'" role="alert">
        <i :class="blockbeeReturnType === 'success' ? 'fas fa-check-circle me-2' : 'fas fa-info-circle me-2'"></i>
        {{ blockbeeReturnMessage }}
        <button type="button" class="btn-close" @click="dismissBlockBeeReturn" aria-label="Close"></button>
      </div>

      <!-- ========== PAGO MÓVIL (sin cambios) ========== -->
      <div v-show="paymentMethod === 'pagomovil'">
      <!-- Información del usuario -->
      <div class="user-info-section" v-if="!loadingAccountInfo">
        <div class="info-item">
          <label>MÉTODO:</label>
          <div class="info-value">
            <span>{{ accountInfo.account_number }}</span>
          </div>
        </div>

        <div class="info-item">
          <label>BANCO:</label>
          <div class="info-value">
            <span>({{ accountInfo.code_bank }}) {{ accountInfo.bank_name }}</span>
            <button type="button" class="copy-btn" aria-label="Copiar código del banco" title="Copiar código del banco" @click="copyToClipboard(accountInfo.code_bank)">
              <i class="fas fa-copy" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        
        <div class="info-item">
          <label>DOCUMENTO:</label>
          <div class="info-value">
            <span>{{ accountInfo.type_identification }}-{{ accountInfo.n_identification }}</span>
            <button type="button" class="copy-btn" aria-label="Copiar documento del titular" title="Copiar documento del titular" @click="copyToClipboard(`${accountInfo.type_identification}-${accountInfo.n_identification}`)">
              <i class="fas fa-copy" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        
        <div class="info-item">
          <label>TELÉFONO:</label>
          <div class="info-value">
            <span>{{ accountInfo.phone }}</span>
            <button type="button" class="copy-btn" aria-label="Copiar teléfono de Pago Móvil" title="Copiar teléfono de Pago Móvil" @click="copyToClipboard(accountInfo.phone)">
              <i class="fas fa-copy" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading state para información de cuenta -->
      <div class="user-info-section" v-else>
        <div class="loading-placeholder">
          <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
          Cargando información de la cuenta...
        </div>
      </div>

      <div class="recharge-instructions" role="note">
        <h3>Antes de recargar</h3>
        <ol>
          <li>Realiza el Pago Móvil a los datos de destino que aparecen arriba.</li>
          <li>Completa el teléfono, banco y fecha desde los que hiciste el pago.</li>
          <li>Ingresa la referencia; el saldo se acredita solo si BDV confirma el pago.</li>
        </ol>
        <p class="mt-2 mb-0 fw-semibold">
          <i class="fas fa-info-circle me-1"></i>
          Importante: el saldo depositado debe jugarse completo (100%) antes de retirar.
        </p>
      </div>

      <!-- Formulario de recarga -->
      <form @submit.prevent="handleRecharge" novalidate>
        <div class="form-group">
          <label for="amount">Monto a Recargar (Bs.)</label>
          <div class="input-group">
            <span class="input-icon"><i class="fas fa-money-bill-wave"></i></span>
            <input 
              type="number" 
              id="amount" 
              name="amount"
              class="form-control" 
              :placeholder="minimumRechargeAmount.toFixed(2)"
              v-model="form.amount"
              required
              autocomplete="off"
              inputmode="decimal"
              :aria-describedby="errors.amount ? 'amount-help amount-error' : 'amount-help'"
              :disabled="isLoading"
              :class="{'is-invalid': errors.amount}"
              @input="validateAmount"
              :min="minimumRechargeAmount"
              step="0.01"
            >
          </div>
          <div v-if="errors.amount" id="amount-error" class="error-message" role="alert" aria-live="polite">
            <i class="fas fa-exclamation-circle"></i> {{ errors.amount }}
          </div>
          <div class="amount-info">
            <small id="amount-help">Monto mínimo: Bs. {{ minimumRechargeAmountDisplay }}</small>
          </div>
        </div>

        <div class="form-group">
          <label for="payerPhone">Teléfono desde el que realizaste el pago</label>
          <input
            type="tel"
            id="payerPhone"
            name="payer_phone"
            class="form-control"
            v-model.trim="form.payer_phone"
            placeholder="0412…"
            inputmode="tel"
            maxlength="11"
            required
            autocomplete="tel"
            :aria-describedby="errors.payer_phone ? 'payer-phone-help payer-phone-error' : 'payer-phone-help'"
            :disabled="isLoading"
            :class="{'is-invalid': errors.payer_phone}"
            @blur="validatePayerPhone"
          >
          <div v-if="errors.payer_phone" id="payer-phone-error" class="error-message" role="alert" aria-live="polite">
            <i class="fas fa-exclamation-circle"></i> {{ errors.payer_phone }}
          </div>
          <small id="payer-phone-help" class="field-help">Usa el formato venezolano 04XXXXXXXXX.</small>
        </div>

        <div class="form-group">
          <label for="originBank">Banco desde el que pagaste</label>
          <select
            id="originBank"
            name="origin_bank_code"
            class="form-control"
            v-model="form.origin_bank_code"
            required
            :disabled="isLoading || isLoadingBanks || originBanks.length === 0"
            :aria-describedby="errors.origin_bank_code ? 'origin-bank-help origin-bank-error' : 'origin-bank-help'"
            :class="{'is-invalid': errors.origin_bank_code}"
            @change="validateOriginBank"
          >
            <option disabled value="">
              {{ isLoadingBanks ? 'Cargando bancos…' : originBanks.length ? 'Selecciona el banco de origen' : 'No hay bancos activos disponibles' }}
            </option>
            <option v-for="bank in originBanks" :key="bank.id" :value="bank.code">
              ({{ bank.code }}) {{ bank.name }}
            </option>
          </select>
          <div v-if="errors.origin_bank_code" id="origin-bank-error" class="error-message" role="alert" aria-live="polite">
            <i class="fas fa-exclamation-circle"></i> {{ errors.origin_bank_code }}
          </div>
          <small id="origin-bank-help" class="field-help">Selecciona el banco desde el que enviaste el Pago Móvil.</small>
        </div>

        <div class="form-group">
          <label for="paymentDate">Fecha del pago</label>
          <input
            type="date"
            id="paymentDate"
            name="payment_date"
            class="form-control"
            v-model="form.payment_date"
            :min="minPaymentDate"
            :max="maxPaymentDate"
            required
            autocomplete="off"
            :aria-describedby="errors.payment_date ? 'payment-date-help payment-date-error' : 'payment-date-help'"
            :disabled="isLoading"
            :class="{'is-invalid': errors.payment_date}"
            @change="validatePaymentDate"
          >
          <div v-if="errors.payment_date" id="payment-date-error" class="error-message" role="alert" aria-live="polite">
            <i class="fas fa-exclamation-circle"></i> {{ errors.payment_date }}
          </div>
          <small id="payment-date-help" class="field-help">Usa la fecha del pago. BDV permite consultar hoy y los tres días anteriores.</small>
        </div>

        <div class="form-group">
          <label for="reference">Referencia del Pago Móvil (últimos 4 dígitos)</label>
          <div class="input-group">
            <span class="input-icon"><i class="fas fa-hashtag"></i></span>
            <input 
              type="text" 
              id="reference" 
              name="reference"
              class="form-control" 
              placeholder="Ej.: 1234…"
              v-model="form.reference"
              required
              autocomplete="off"
              spellcheck="false"
              :aria-describedby="errors.reference ? 'reference-help reference-error' : 'reference-help'"
              inputmode="numeric"
              pattern="[0-9]{4,8}"
              :disabled="isLoading"
              :class="{'is-invalid': errors.reference}"
              @blur="validateReference"
              maxlength="8"
            >
          </div>
          <div v-if="errors.reference" id="reference-error" class="error-message" role="alert" aria-live="polite">
            <i class="fas fa-exclamation-circle"></i> {{ errors.reference }}
          </div>
          <small id="reference-help" class="field-help">Ingresa al menos los últimos 4 dígitos. Si tu banco muestra una referencia de 5 a 8 dígitos, puedes copiarla completa; conserva los ceros iniciales.</small>
        </div>

        <p class="small text-muted">El saldo solo se acredita si BDV confirma la referencia y el monto. El comprobante no sustituye la conciliación.</p>

        <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading || loadingAccountInfo || isLoadingBanks">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <i v-else class="fas fa-credit-card me-2"></i> 
          {{ isLoading ? 'Verificando con BDV...' : 'Verificar pago y recargar' }}
        </button>
      </form>

      <!-- Información importante -->
      <div class="important-info" v-if="!loadingAccountInfo">
        <h4>Información Importante:</h4>
        <ul>
          <li>El monto mínimo es de <strong>Bs. {{ minimumRechargeAmountDisplay }}</strong>.</li>
          <li>El saldo se acredita una sola vez cuando BDV confirma la referencia y el monto.</li>
          <li>Si el resultado queda en revisión o no se confirma, no vuelvas a pagar; contacta a soporte.</li>
        </ul>
      </div>
      </div>
      <!-- Fin Pago Móvil -->

      <!-- ========== BLOCKBEE ========== -->
      <div v-show="paymentMethod === 'blockbee'" class="blockbee-section">
        <p class="method-description">Recarga tu saldo pagando con <strong>USDT</strong> a través de BlockBee.</p>

        <!-- Advertencia de red -->
        <div class="blockbee-warning">
          <div class="blockbee-warning-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="blockbee-warning-text">
            <strong>¡Atención! Solo aceptamos USDT por la red BEP20 (Binance Smart Chain).</strong>
            <span>Enviar por otra red (como Tron o Ethereum) resultará en la <strong>pérdida total de tus fondos</strong>.</span>
          </div>
        </div>

        <div v-if="blockbeePaymentUrl" class="blockbee-payment-box">
          <div class="payment-ready">
            <i class="fas fa-check-circle text-success" style="font-size:2rem;"></i>
            <h4 class="mt-2">Enlace de pago generado</h4>
            <p>Haz clic en el botón para completar tu pago con <strong>USDT BEP20</strong>.</p>
            <a :href="blockbeePaymentUrl" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-block">
              <i class="fas fa-external-link-alt me-2"></i> Ir a pagar en BlockBee
            </a>
            <p class="mt-3 small" style="color:#495057;">Cuando termines de pagar, tu transacción quedará en revisión y un administrador la aprobará en breve.</p>
            <a
              :href="whatsappBlockbeeNotifyUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-whatsapp-notify btn-block mt-3"
            >
              <i class="fab fa-whatsapp me-2"></i> Notifica aquí
            </a>
            <button type="button" class="btn btn-outline-secondary btn-sm mt-2" @click="blockbeePaymentUrl = null; blockbeeForm.amount = ''">
              Crear otra recarga
            </button>
          </div>
        </div>

        <form v-else @submit.prevent="handleBlockBeeRecharge" class="blockbee-form">
          <div class="form-group">
            <label for="blockbee-amount">Monto (USD)</label>
            <div class="input-group">
              <span class="input-icon"><i class="fas fa-dollar-sign"></i></span>
              <input
                type="number"
                id="blockbee-amount"
                v-model="blockbeeForm.amount"
                class="form-control"
                placeholder="10.00"
                min="1"
                step="0.01"
                required
                :disabled="blockbeeLoading"
              >
            </div>
            <div class="amount-info"><small>Monto mínimo: $1.00 USD</small></div>
          </div>
          <div v-if="blockbeeError" class="alert alert-danger py-2">{{ blockbeeError }}</div>
          <button type="submit" class="btn btn-primary btn-block" :disabled="blockbeeLoading || !blockbeeForm.amount || parseFloat(blockbeeForm.amount) < 1">
            <span v-if="blockbeeLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <i v-else class="fas fa-link me-2"></i>
            {{ blockbeeLoading ? 'Generando enlace...' : 'Generar enlace de pago' }}
          </button>
          <p class="whatsapp-notify-hint mt-3">Si ya realizaste el pago en BlockBee, notifica aquí con el monto en USD:</p>
          <a
            :href="whatsappBlockbeeNotifyUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-whatsapp-notify btn-block"
          >
            <i class="fab fa-whatsapp me-2"></i> Notifica aquí
          </a>
        </form>

        <div class="important-info mt-4">
          <h4>Información:</h4>
          <ul>
            <li>Solo se acepta <strong>USDT por la red BEP20</strong> (Binance Smart Chain).</li>
            <li>El monto se ingresa en USD y se convierte automáticamente a USDT.</li>
            <li>Tu transacción quedará en revisión hasta que un administrador la apruebe.</li>
          </ul>
        </div>
      </div>
      <!-- Fin BlockBee -->

    </div>

    <teleport to="body">
      <div
        v-if="showPostRechargeModal"
        class="post-recharge-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="post-recharge-title"
      >
        <div class="post-recharge-modal" @click.stop>
          <div class="post-recharge-modal-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h3 id="post-recharge-title" class="post-recharge-modal-title">¡Pago confirmado!</h3>
          <p class="post-recharge-modal-text">
            BDV confirmó el pago y el saldo fue acreditado. Cierra este aviso para volver al panel.
          </p>
          <div class="post-recharge-modal-actions">
            <button
              type="button"
              class="btn btn-post-recharge-secondary"
              @click="closePostRechargeModalAndRedirect"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.recharge-container {
  margin-top: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.recharge-card {
  width: 100%;
  max-width: 800px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  padding: 40px;
  transition: transform 0.3s ease;
}

.recharge-card:hover {
  transform: translateY(-5px);
}

.recharge-header {
  text-align: center;
  margin-bottom: 30px;
}

.recharge-header h2 {
  color: #2c3e50;
  font-weight: 700;
  margin-bottom: 10px;
}

.recharge-header p {
  color: #7f8c8d;
  font-size: 16px;
}

.payment-method-selector {
  display: flex;
  gap: 10px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0;
}

.method-tab {
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s ease;
  border-radius: 8px 8px 0 0;
}

.method-tab:hover {
  color: #e67e22;
  background: #fef7f0;
}

.method-tab.active {
  color: #e67e22;
  border-bottom-color: #e67e22;
  background: #fef7f0;
}

.method-tab-logo {
  height: 22px;
  width: auto;
  vertical-align: middle;
  margin-right: 8px;
  display: inline-block;
}

.selected-method-indicator {
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 2px solid #e9ecef;
}

.method-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.method-crypto {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.method-fiat {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
}

.text-muted {
  color: #6c757d;
  font-size: 12px;
}

.method-loading {
  text-align: center;
  padding: 40px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 24px;
  border: 2px solid #e9ecef;
}

.method-loading .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

.method-loading p {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.blockbee-section .method-description {
  color: #495057;
  margin-bottom: 16px;
  font-size: 15px;
}

.blockbee-warning {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fffbe6;
  border: 2px solid #f5a623;
  border-left: 6px solid #f5a623;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 24px;
}

.blockbee-warning-icon {
  font-size: 1.6rem;
  color: #f5a623;
  flex-shrink: 0;
  margin-top: 2px;
}

.blockbee-warning-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #7a4f00;
  line-height: 1.5;
}

.blockbee-warning-text strong {
  font-size: 15px;
  color: #5a3700;
}

.blockbee-payment-box {
  background: #f0f9f0;
  border: 1px solid #c3e6c3;
  border-radius: 10px;
  padding: 24px;
  margin-bottom: 20px;
  text-align: center;
}

.blockbee-payment-box .payment-ready h4 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.blockbee-payment-box .payment-ready p {
  color: #495057;
}

.blockbee-form .form-control {
  height: 50px;
  border-radius: 10px;
  padding-left: 45px;
}

.user-info-section {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
}

.recharge-instructions {
  margin: -8px 0 24px;
  padding: 16px 20px;
  border: 1px solid #f2d8c0;
  border-left: 4px solid #e67e22;
  border-radius: 10px;
  background: #fffaf5;
  color: #495057;
}

.recharge-instructions h3 {
  margin: 0 0 8px;
  color: #2c3e50;
  font-size: 1rem;
  font-weight: 700;
}

.recharge-instructions ol {
  margin: 0;
  padding-left: 1.25rem;
}

.recharge-instructions li + li {
  margin-top: 4px;
}

.field-help {
  display: block;
  margin-top: 6px;
  color: #5f6b76;
  font-size: 0.875rem;
  line-height: 1.45;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

.info-item label {
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.info-value {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-value span {
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #495057;
}

.copy-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 5px;
  border-radius: 5px;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.copy-btn:hover {
  color: #e67e22;
  background: #f8f9fa;
}

.copy-btn:focus-visible {
  outline: 3px solid rgba(230, 126, 34, 0.55);
  outline-offset: 2px;
}

.form-group {
  margin-bottom: 25px;
  position: relative;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 15px;
  color: #7f8c8d;
  z-index: 1;
}

.form-control {
  height: 50px;
  border-radius: 10px;
  border: 2px solid #e0e0e0;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  padding-left: 45px;
}

.form-control:focus {
  border-color: #e67e22;
  box-shadow: 0 0 0 0.2rem rgba(230, 126, 34, 0.25);
}

.error-message {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

.amount-info {
  margin-top: 5px;
}

.amount-info small {
  color: #6c757d;
}

.file-upload-container {
  position: relative;
}

.file-input {
  display: none;
}

.file-upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.file-upload-area:hover {
  border-color: #e67e22;
  background: #fef7f0;
}

.upload-placeholder {
  color: #6c757d;
}

.upload-placeholder i {
  font-size: 2rem;
  margin-bottom: 10px;
  color: #e67e22;
}

.upload-placeholder p {
  margin: 10px 0 5px 0;
  font-weight: 600;
  color: #2c3e50;
}

.upload-placeholder small {
  color: #6c757d;
  font-size: 0.85rem;
}

.file-preview {
  text-align: left;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 10px;
  background: white;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
}

.file-info i {
  color: #e67e22;
  font-size: 1.2rem;
}

.file-info span {
  flex: 1;
  color: #2c3e50;
  font-weight: 500;
  word-break: break-all;
}

.remove-file {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.remove-file:hover {
  background: #c82333;
  transform: scale(1.1);
}

.image-preview {
  max-width: 200px;
  margin: 0 auto;
}

.image-preview img {
  width: 100%;
  height: auto;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
  max-height: 150px;
  object-fit: cover;
}

.bank-selection {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 10px;
}

.bank-option {
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background: white;
}

.bank-option:hover {
  border-color: #e67e22;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.bank-option.selected {
  border-color: #e67e22;
  background: #fef7f0;
}

.bank-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  margin-bottom: 10px;
}

.bank-logo img {
  max-height: 40px;
  max-width: 100%;
}

.bank-placeholder {
  background: #e9ecef;
  color: #6c757d;
  padding: 8px 12px;
  border-radius: 5px;
  font-weight: 600;
  font-size: 14px;
}

.bank-info {
  text-align: center;
  position: relative;
}

.bank-name {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
  margin-bottom: 5px;
}

.bank-code {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  font-size: 12px;
  margin-bottom: 10px;
}

.new-tag {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e67e22;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
}

.btn-primary {
  background-color: #e67e22;
  border-color: #e67e22;
  height: 50px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background-color: #d35400;
  border-color: #d35400;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:disabled {
  background-color: #f0ad4e;
  border-color: #f0ad4e;
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
}

.whatsapp-notify-hint {
  margin: 16px 0 10px;
  font-size: 14px;
  color: #495057;
  line-height: 1.45;
  text-align: center;
}

.btn-whatsapp-notify {
  background-color: #25d366;
  border: none;
  color: #fff;
  height: 50px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-whatsapp-notify:hover {
  background-color: #20ba5a;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(37, 211, 102, 0.35);
}

.important-info {
  background: #e8f4fd;
  border-left: 4px solid #e67e22;
  border-radius: 5px;
  padding: 20px;
  margin-top: 30px;
}

.important-info h4 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 16px;
}

.important-info ul {
  margin: 0;
  padding-left: 20px;
}

.important-info li {
  color: #495057;
  margin-bottom: 8px;
  font-size: 14px;
}

.important-info strong {
  color: #e67e22;
}

.alert {
  border-radius: 10px;
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .recharge-container {
    padding: 80px 10px 20px 10px;
  }
  
  .recharge-card {
    padding: 20px 15px;
  }
  
  .bank-selection {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* Modal tras solicitar recarga (Pago Móvil) */
.post-recharge-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
}

.post-recharge-modal {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px 24px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.post-recharge-modal-icon {
  font-size: 3rem;
  color: #22c55e;
  line-height: 1;
  margin-bottom: 12px;
}

.post-recharge-modal-title {
  margin: 0 0 12px;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
}

.post-recharge-modal-text {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.5;
  color: #475569;
}

.post-recharge-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-post-recharge-primary {
  width: 100%;
  justify-content: center;
  min-height: 48px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-post-recharge-secondary {
  width: 100%;
  min-height: 48px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.btn-post-recharge-secondary:hover {
  background: #e2e8f0;
  border-color: #cbd5e1;
}
</style>

<script>
import api from '@/services/api';
import EventBus from '@/EventBus';
import { mapGetters } from 'vuex';
import {
  buildRechargeReportMessage,
  rechargeWhatsAppUrl
} from '@/utils/rechargeWhatsApp';

export default {
  name: 'RechargeView',
  data() {
    const localDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const today = new Date();
    const earliestPaymentDate = new Date(today);
    earliestPaymentDate.setDate(earliestPaymentDate.getDate() - 3);

    return {
      paymentMethod: 'pagomovil',
      form: {
        amount: '',
        reference: '',
        payer_phone: '',
        payment_date: localDate(today),
        origin_bank_code: ''
      },
      minPaymentDate: localDate(earliestPaymentDate),
      maxPaymentDate: localDate(today),
      originBanks: [],
      isLoadingBanks: false,
      minimumRechargeAmount: 1000,
      isLoading: false,
      loadingAccountInfo: true,
      errorMessage: '',
      successMessage: '',
      errors: {},
      // BlockBee
      blockbeeForm: { amount: '' },
      blockbeeLoading: false,
      blockbeePaymentUrl: null,
      blockbeeError: '',
      blockbeeReturnMessage: '',
      blockbeeReturnType: 'success',
      accountInfo: {
        account_id: null,
        account_number: '',
        code_bank: '',
        bank_name: '',
        type_identification: '',
        n_identification: '',
        phone: ''
      },
      lastRechargeMethod: 'pagomovil',
      showOnlyLastMethod: true,
      // Temporal: solo Pago Móvil; BlockBee y lock por última recarga desactivados
      blockbeeTemporarilyDisabled: true,
      isLoadingMethod: false,
      showPostRechargeModal: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),

    displayUserName() {
      if (this.currentUser?.name) return this.currentUser.name;
      if (this.currentUser?.email) return this.currentUser.email.split('@')[0];
      return '';
    },

    /** BlockBee desactivado por ahora: solo Pago Móvil. */
    showsBlockbeeForUser() {
      return false;
    },

    whatsappBlockbeeNotifyUrl() {
      const amt = this.blockbeeForm.amount;
      const amountStr = amt && String(amt).trim() !== ''
        ? `US$ ${amt} (USDT)`
        : '';
      const msg = buildRechargeReportMessage({
        userName: this.displayUserName,
        amount: amountStr,
        methodLabel: 'USDT'
      });
      return rechargeWhatsAppUrl(msg);
    },

    isFormValid() {
      const { amount, reference, payer_phone, payment_date, origin_bank_code } = this.form;
      const requiredFieldsFilled = amount && reference && payer_phone && payment_date && origin_bank_code;
      const noErrors = Object.keys(this.errors).length === 0;
      const amountValid = /^\d{1,8}(?:\.\d{1,2})?$/.test(String(amount)) && Number(amount) >= this.minimumRechargeAmount;
      const referenceValid = /^\d{4,8}$/.test(String(reference));
      const payerPhoneValid = /^04\d{9}$/.test(String(payer_phone));

      return requiredFieldsFilled
        && this.accountInfo.account_id
        && this.originBanks.length > 0
        && amountValid
        && referenceValid
        && payerPhoneValid
        && noErrors;
    },

    minimumRechargeAmountDisplay() {
      return Number(this.minimumRechargeAmount).toLocaleString('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  },
  async mounted() {
    this.forcePagoMovilOnly();
    this.handleBlockBeeReturnQuery();
    await Promise.all([this.loadAccountInfo(), this.loadOriginBanks()]);
  },
  methods: {
    /** Fuerza Pago Móvil: sin BlockBee ni preferencia por última recarga. */
    forcePagoMovilOnly() {
      this.paymentMethod = 'pagomovil';
      this.lastRechargeMethod = 'pagomovil';
      this.showOnlyLastMethod = true;
      this.isLoadingMethod = false;
      this.blockbeePaymentUrl = null;
      this.blockbeeError = '';
    },

    /** Alinea valores del API (p. ej. pago_movil) con las claves usadas en la vista (pagomovil). */
    normalizePaymentMethod(raw) {
      if (raw == null || raw === '') return 'pagomovil';
      const m = String(raw).toLowerCase().replace(/\s+/g, '_');
      if (m === 'pago_movil' || m === 'pagomovil') return 'pagomovil';
      // BlockBee desactivado: cualquier otro método cae a Pago Móvil
      return 'pagomovil';
    },

    closePostRechargeModalAndRedirect() {
      this.showPostRechargeModal = false;
      this.$router.push('/dashboard');
    },

    // Última recarga desactivada: siempre Pago Móvil
    async loadLastRecharge() {
      this.forcePagoMovilOnly();
    },

    /**
     * Solo Pago Móvil por ahora (BlockBee y lock por historial desactivados).
     */
    applyBlockbeeTemporaryPolicy() {
      this.forcePagoMovilOnly();
    },

    // Cargar información de la cuenta del usuario
    async loadAccountInfo() {
      try {
        this.loadingAccountInfo = true;
        const response = await api.get('/api/accounts_recharge');
        const configuredMinimum = Number(response.data.minimum_recharge_amount);
        if (Number.isFinite(configuredMinimum) && configuredMinimum > 0) {
          this.minimumRechargeAmount = configuredMinimum;
        }
        const destinationAccount = response.data.accounts?.[0];
        if (!destinationAccount) {
          throw new Error('No active Pago Móvil destination account is configured.');
        }
        
        this.accountInfo = {
          account_id: destinationAccount.id || destinationAccount.account_id,
          account_number: destinationAccount.account_number,
          code_bank: destinationAccount.bank.code,
          bank_name: destinationAccount.bank.name,
          type_identification: destinationAccount.type_identification,
          n_identification: destinationAccount.n_identification,
          phone: destinationAccount.phone
        };
      } catch (error) {
        this.errorMessage = 'Error al cargar la información de tu cuenta. Por favor, intenta de nuevo.';
      } finally {
        this.loadingAccountInfo = false;
      }
    },

    async loadOriginBanks() {
      this.isLoadingBanks = true;
      try {
        const response = await api.get('/api/banks');
        this.originBanks = Array.isArray(response.data)
          ? response.data.slice().sort((a, b) => String(a.code).localeCompare(String(b.code), 'es', { numeric: true }))
          : [];
      } catch (error) {
        this.errorMessage = 'No se pudo cargar la lista de bancos. Intenta recargar la página.';
      } finally {
        this.isLoadingBanks = false;
      }
    },
    
    validateAmount() {
      const amount = String(this.form.amount || '');
      
      if (!this.form.amount) {
        this.errors.amount = 'El monto es obligatorio';
        return;
      }
      
      if (!/^\d{1,8}(?:\.\d{1,2})?$/.test(amount)) {
        this.errors.amount = 'Ingresa un monto válido con hasta dos decimales';
        return;
      }

      if (Number(amount) < this.minimumRechargeAmount) {
        this.errors.amount = `El monto mínimo es de Bs. ${this.minimumRechargeAmountDisplay}`;
        return;
      }
      
      delete this.errors.amount;
    },
    
    validateReference() {
      if (!this.form.reference) {
        this.errors.reference = 'El número de referencia es obligatorio';
        return;
      }
      
      if (!/^\d{4,8}$/.test(String(this.form.reference))) {
        this.errors.reference = 'La referencia debe contener entre 4 y 8 dígitos';
        return;
      }
      
      delete this.errors.reference;
    },

    validatePayerPhone() {
      if (!/^04\d{9}$/.test(String(this.form.payer_phone || ''))) {
        this.errors.payer_phone = 'Ingresa el teléfono pagador en formato 04XXXXXXXXX';
        return;
      }
      delete this.errors.payer_phone;
    },

    validatePaymentDate() {
      const date = String(this.form.payment_date || '');
      if (!date || date < this.minPaymentDate || date > this.maxPaymentDate) {
        this.errors.payment_date = 'Selecciona hoy o una fecha de los tres días anteriores';
        return;
      }
      delete this.errors.payment_date;
    },

    validateOriginBank() {
      if (!this.form.origin_bank_code) {
        this.errors.origin_bank_code = 'Selecciona el banco desde el que pagaste';
        return;
      }
      delete this.errors.origin_bank_code;
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        // Mostrar notificación de éxito
        this.$toast?.success('Copiado al portapapeles');
      } catch (error) {
        console.error('Error al copiar:', error);
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    },
    
    async handleRecharge() {
      this.errors = {};
      this.validateAmount();
      this.validateReference();
      this.validatePayerPhone();
      this.validatePaymentDate();
      this.validateOriginBank();

      if (!this.isFormValid) {
        this.errorMessage = 'Revisa los datos del pago antes de continuar.';
        this.$nextTick(() => this.$el.querySelector('.is-invalid')?.focus());
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const { data } = await api.post('/api/recharge/bdv', {
          amount: String(this.form.amount),
          account_id: this.accountInfo.account_id,
          reference: this.form.reference,
          payer_phone: this.form.payer_phone,
          payment_date: this.form.payment_date,
          origin_bank_code: this.form.origin_bank_code
        });

        if (data?.status !== 'confirmed') {
          this.errorMessage = data?.message || 'BDV no confirmó el pago. No se acreditó saldo.';
          return;
        }

        const oldBalance = Number(this.currentUser?.balance || 0);
        const newBalance = Number(data.balance);
        await this.$store.dispatch('auth/updateUserData', { balance: data.balance });
        this.$store.commit('games/SET_USER_BALANCE', newBalance);
        window.dispatchEvent(new CustomEvent('balance-updated', {
          detail: {
            oldBalance,
            newBalance,
            difference: newBalance - oldBalance,
            source: 'recharge',
            notificationType: 'success',
            message: 'Recarga Pago Móvil conciliada.'
          }
        }));

        EventBus.emit('show-notification', {
          type: 'success',
          message: 'Pago conciliado y saldo acreditado.',
          duration: 4000
        });

        this.form.amount = '';
        this.form.reference = '';
        this.showPostRechargeModal = true;
      } catch (error) {
        this.handleRechargeError(error);
      } finally {
        this.isLoading = false;
      }
    },
    
    handleRechargeError(error) {
      let errorMessage = '';
      
      if (error.response && error.response.status === 422 && error.response.data?.errors) {
        this.errors = error.response.data.errors;
        errorMessage = error.response.data?.message || 'Revisa los datos del pago.';
      } else if (error.response) {
        // Otros errores del servidor
        errorMessage = error.response.data?.message || 
                      `Error del servidor: ${error.response.status} ${error.response.statusText}`;
      } else {
        // Errores de red u otros
        errorMessage = error.message || 'No se pudo conectar con el servidor. Verifica tu conexión.';
      }
      
      this.errorMessage = errorMessage;
      
      // Mostrar toast de error
      EventBus.emit('show-notification', {
        type: 'error',
        message: errorMessage,
        duration: 5000
      });
    },

    handleBlockBeeReturnQuery() {
      const q = this.$route?.query || {};
      const payment = q.payment;
      if (payment !== 'success') return;

      // BlockBee desactivado: avisar y quedarse en Pago Móvil
      this.blockbeeReturnMessage = 'Pago recibido. Tu transacción quedará en revisión hasta que un administrador la apruebe.';
      this.blockbeeReturnType = 'success';
      this.forcePagoMovilOnly();

      if (this.$router?.replace) {
        const query = { ...this.$route.query };
        delete query.payment;
        delete query.order_id;
        delete query.payment_id;
        this.$router.replace({ path: this.$route.path, query: Object.keys(query).length ? query : undefined });
      }
    },

    dismissBlockBeeReturn() {
      this.blockbeeReturnMessage = '';
      this.blockbeeReturnType = 'success';
    },

    async handleBlockBeeRecharge() {
      const amount = parseFloat(this.blockbeeForm.amount);
      if (!amount || amount < 1) {
        this.blockbeeError = 'El monto mínimo es $1.00 USD.';
        return;
      }
      this.blockbeeError = '';
      this.blockbeeLoading = true;
      try {
        const { data } = await api.post('/api/recharge/blockbee/create', {
          amount: amount.toFixed(2)
        });
        if (data?.success && data?.payment_url) {
          this.blockbeePaymentUrl = data.payment_url;
        } else {
          this.blockbeeError = data?.message || 'No se recibió el enlace de pago. Revisa el backend.';
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Error al generar el enlace de pago.';
        this.blockbeeError = msg;
      } finally {
        this.blockbeeLoading = false;
      }
    }
  }
};
</script>
